import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MonitorPlay, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = "/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
        remember_me: rememberMe,
      });

      if (response.data.success) {
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("auth_username", response.data.username);
        localStorage.setItem("auth_expires", response.data.expires_at);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <MonitorPlay className="w-10 h-10 text-white" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tighter">
              Live Channels
            </h1>
          </div>
          <p className="text-white/50 mt-2">Connectez-vous pour accéder aux chaînes</p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
        >
          <h2 className="text-white text-xl font-semibold mb-6">Connexion</h2>

          {error && (
            <div
              data-testid="login-error"
              className="flex items-center gap-3 p-4 mb-6 bg-red-500/10 border border-red-500/30 text-red-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="username" className="block text-white/70 text-sm mb-2">
              Identifiant
            </label>
            <input
              type="text"
              id="username"
              data-testid="username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Entrez votre identifiant"
              className="w-full px-4 py-3 bg-black border border-white/20 text-white placeholder-white/30 focus:border-white focus:outline-none transition-colors"
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-white/70 text-sm mb-2">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                className="w-full px-4 py-3 pr-12 bg-black border border-white/20 text-white placeholder-white/30 focus:border-white focus:outline-none transition-colors"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-5 h-5 border border-white/30 bg-black peer-checked:bg-white peer-checked:border-white transition-colors" />
                <svg
                  className="absolute top-0.5 left-0.5 w-4 h-4 text-black opacity-0 peer-checked:opacity-100 transition-opacity"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-white/60 text-sm group-hover:text-white/80 transition-colors">
                Se souvenir de moi (30 jours)
              </span>
            </label>
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading || !username || !password}
            className="w-full px-6 py-3 bg-white text-black font-bold uppercase tracking-wider hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connexion...
              </>
            ) : (
              "Se connecter"
            )}
          </button>
        </form>

        <p className="text-center text-white/30 text-sm mt-6">Système de chaînes privées</p>
      </div>
    </div>
  );
}
